import {createElement, PropTypes} from 'rlink-react';
import {FormattedMessage, FormattedHTMLMessage} from 'react-intl';
import Main from 'components/main';
import AbilityCard from 'components/ability-card';
import UnitRevealCard from 'components/unit-reveal-card';
import ImageCarousel from 'components/image-carousel';
import css from 'static/style/dow.less';
import contentLoader from 'helpers/content-loader';
import {NavLink} from 'rlink-router/component';
import {DOW} from 'terms';
import DocumentTitle from 'rlink-react-document/DocumentTitle.jsx';
import DocumentOpenGraphProperty from 'rlink-react-document/DocumentOpenGraphProperty.jsx';
import LegacyScrollTransition from 'rlink-transitions/LegacyScrollTransition';
import TrackViewOnVisible from 'rlink-telemetry/TrackViewOnVisible';

import IntroPanel from 'components/IntroPanel';
import EliteDescriptionPanel from 'components/EliteDescriptionPanel';

export default function (params, content) {
  if (typeof document !== 'undefined') {
    document.body.id = css.faction;
  }

  let factionData = content.factionSettings.data;
  let faction = params.faction;
  let elite = '';
  let factionName = '';

  switch (faction) {
    case 'spacemarines':
      elite = 'gabriel';
      factionName = 'Space Marines';
      break;
    case 'eldar':
      elite = 'macha';
      factionName = 'Eldar';
      break;
    case 'orks':
      elite = 'gorgutz';
      factionName = 'Orks';
      break;
  }

  let factionDetails = <FormattedMessage
    id="ui.text.info.FACTION_PAGE_DESCRIPTION"
    defaultMessage="{factionName} faction details"
    description="Description for the faction page"
    values={{factionName}} />;

  return (
    <Main globalSettings={content.globalSettings}>
      <DocumentTitle title={factionName} site={DOW} />
      <DocumentOpenGraphProperty property="url" value={`https://www.dawnofwar.com/${faction}`} />
      <DocumentOpenGraphProperty property="title" value={factionName} />
      <DocumentOpenGraphProperty property="description" value={factionDetails} />
      <DocumentOpenGraphProperty property="image" value={contentLoader(`${elite}-panel-1080.jpg`)} />
      <div className={css[`type_${faction}`]} key={`faction:${faction}`}>
        <main id={css.layout_faction}>
          <section className={css['faction-cover']}>
            <div className={css['panel-bg']}>
              <picture>
                <source srcSet={contentLoader(`${elite}-main-320.jpg`)} type="image/jpeg" media="(max-height:320px)" />
                <source srcSet={contentLoader(`${elite}-main-420.jpg`)} type="image/jpeg" media="(min-height:321px) and (max-height:420px)" />
                <source srcSet={contentLoader(`${elite}-main-768.jpg`)} type="image/jpeg" media="(min-height:421px) and (max-height:768px)" />
                <source srcSet={contentLoader(`${elite}-main-1024.jpg`)} type="image/jpeg" media="(min-height:769px) and (max-height:1024px)" />
                <source srcSet={contentLoader(`${elite}-main-1080.jpg`)} type="image/jpeg" media="(min-height:1025px)" />

                <img src={contentLoader(`${elite}-main-1080.jpg`)} className={css['bg-media']} id="cover_poster" />
              </picture>
            </div>
          </section>
          <section className={css['intro-panel']}>
            <IntroPanel faction={faction} css={css} />
          </section>

          <section className={css.vignette}>
            <div className={css['panel-bg']}>
              <picture>
                <source srcSet={contentLoader(`${faction}-vignette-preview-320.jpg`)} type="image/jpeg" media="(max-height:320px)" />
                <source srcSet={contentLoader(`${faction}-vignette-preview-420.jpg`)} type="image/jpeg" media="(min-height:321px) and (max-height:420px)" />
                <source srcSet={contentLoader(`${faction}-vignette-preview-460.jpg`)} type="image/jpeg" media="(min-height:421px)" />

                <img src={contentLoader(`${faction}-vignette-preview-460.jpg`)} className={css['bg-media']} />
              </picture>
              <video
                id="cover_video"
                className={`${css['bg-media']} ${css.autoplay}`}
                loop
                muted
                preload="auto"
                autoPlay>
                <source src={contentLoader(`${faction}-preview.webm`, 'video')} type="video/webm" />
                <source src={contentLoader(`${faction}-preview.mp4`, 'video')} type="video/mp4" />
              </video>
            </div>

          </section>

          <LegacyScrollTransition css={css}>
            <section className={css['elite-dark-panel']}>
              <div className={css['panel-bg']}>
                <picture>
                  <source srcSet={contentLoader(`${elite}-panel-320.jpg`)} type="image/jpeg" media="(max-height:320px)" />
                  <source srcSet={contentLoader(`${elite}-panel-420.jpg`)} type="image/jpeg" media="(min-height:321px) and (max-height:420px)" />
                  <source srcSet={contentLoader(`${elite}-panel-768.jpg`)} type="image/jpeg" media="(min-height:421px) and (max-height:768px)" />
                  <source srcSet={contentLoader(`${elite}-panel-1024.jpg`)} type="image/jpeg" media="(min-height:769px) and (max-height:1024px)" />
                  <source srcSet={contentLoader(`${elite}-panel-1080.jpg`)} type="image/jpeg" media="(min-height:1025px)" />

                  <img src={contentLoader(`${elite}-panel-1080.jpg`)} className={css['bg-media']} id="cover_poster" />
                </picture>
              </div>
              <LegacyScrollTransition css={css}>
                <div className={css['panel-content']}>
                  <div className={css.column}>
                    <img src={contentLoader(`${elite}-icon.png`)} />
                  </div>
                  <EliteDescriptionPanel faction={faction} css={css} />
                </div>
              </LegacyScrollTransition>
            </section>
          </LegacyScrollTransition>

          <LegacyScrollTransition css={css}>
            <section ref={el => {
              let darkPanel = document.querySelector(`.${css['elite-dark-panel']}`);
              if (el) {
                el.addEventListener('reached-top-viewport', () => {
                  darkPanel.classList.add(css['no-longer-sticky']);
                });

                el.addEventListener('reached-top-quart-1', () => {
                  darkPanel.classList.remove(css['no-longer-sticky']);
                });
              }
            }}
            className={css['elite-full-panel']}>
              <div className={css['panel-bg']} />
              <LegacyScrollTransition css={css}>
                <div className={css['panel-content']} id="elites">
                  <TrackViewOnVisible name="elites" />
                  <h1>
                    <FormattedMessage
                      id="ui.label.title.FACTION_ABILITIES"
                      defaultMessage="abilities"
                      description="Faction hero abilities section title" />
                  </h1>
                  <div className={css['ability-descriptions']}>
                    {factionData.get('abilities').map(ability => (
                      <AbilityCard key={ability.get('mediaName')} {...ability.toJS()} />
                    ))}
                  </div>
                </div>
              </LegacyScrollTransition>
            </section>
          </LegacyScrollTransition>

          <LegacyScrollTransition css={css}>
            <section className={css['blog-cta']}>
              <div className={css['panel-content']}>
                <h1>
                  <FormattedHTMLMessage
                    id="ui.label.title.COMMUNITY_BLOG_SPOTLIGHT"
                    defaultMessage={'Follow our community blog for detailed <span class="{HIGHLIGHT_CLASSNAME}">unit spotlights</span>'}
                    description="Heading for the community blog section. {HIGHLIGHT_CLASSNAME} is used to add a highlight to part of the title."
                    values={{
                      HIGHLIGHT_CLASSNAME: css.highlight,
                    }} />
                </h1>
                <p>
                  <NavLink className={`${css.btn} ${css['btn-secondary']}`}
                    href={{
                      view: 'news',
                      returnTransition: 'fade',
                      transition: 'fade',
                    }}>
                    <FormattedMessage
                      id="ui.label.link.COMMUNITY_BLOG"
                      defaultMessage="community blog"
                      description="Faction community blog section title" />
                  </NavLink>
                </p>
              </div>
            </section>
          </LegacyScrollTransition>

          <LegacyScrollTransition css={css}>
            <section className={css['unit-reveals']}>
              <div className={css['panel-content']}>
                {factionData.get('unitReveals').map(unitReveal => (<UnitRevealCard key={unitReveal.get('articleSlug')} {...unitReveal.toJS()} />))}
              </div>
            </section>
          </LegacyScrollTransition>

          <section className={css['gallery-panel']}>
            <ImageCarousel css={css}
              arrowIcon={contentLoader('arrow-right.png')}
              items={factionData.get('imageCarousel').toJS()} />
          </section>
        </main>
      </div>

    </Main>
  );
}



// WEBPACK FOOTER //
// ./apps/www.dawnofwar.com/src/views/faction.jsx